import { PageContainer, InputFiled, TextArea, Spinner } from "@/components";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import {
  useCollegeProfileQuery,
  useUpdateCollegeProfileMutation,
} from "@/hooks/api/college.queries";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const profileSchema = z.object({
  accreditation: z.string().min(1, "Accreditation is required"),
  established_year: z
    .number()
    .min(1800, "Invalid year")
    .max(new Date().getFullYear()),
  total_students: z.number().min(0, "Total students cannot be negative"),
  website: z.string().url("Invalid website URL"),
  description: z
    .string()
    .min(10, "Description should be at least 10 characters"),
});

type ProfileFormType = z.infer<typeof profileSchema>;

const CollegeProfile = () => {
  const { data: profile, isLoading } = useCollegeProfileQuery();
  const updateMutation = useUpdateCollegeProfileMutation();

  const form = useForm<ProfileFormType>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      accreditation: "",
      established_year: new Date().getFullYear(),
      total_students: 0,
      website: "",
      description: "",
    },
  });

  useEffect(() => {
    if (profile) {
      form.reset({
        accreditation: profile.accreditation || "",
        established_year: profile.established_year || 0,
        total_students: profile.total_students || 0,
        website: profile.website || "",
        description: profile.description || "",
      });
    }
  }, [profile, form]);

  const onSubmit = (data: ProfileFormType) => {
    updateMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <PageContainer className="space-y-6 pb-10">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-gray-500">Settings</span>
          <h1 className="text-2xl font-bold">College Profile</h1>
        </div>
      </div>

      <div className="bg-card text-card-foreground overflow-hidden rounded-xl border shadow-sm">
        <div className="bg-muted/30 border-b px-6 py-4">
          <h2 className="text-lg font-semibold">General Information</h2>
          <p className="text-muted-foreground text-sm">
            {`Update your college's basic information and public details.`}
          </p>
        </div>

        <div className="p-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <InputFiled
                  type="text"
                  name="accreditation"
                  label="Accreditation"
                  placeholder="e.g. NAAC A++, NBA"
                  required
                  disabled={updateMutation.isPending}
                />

                <InputFiled
                  type="number"
                  name="established_year"
                  label="Established Year"
                  placeholder="e.g. 1995"
                  required
                  disabled={updateMutation.isPending}
                />

                <InputFiled
                  type="number"
                  name="total_students"
                  label="Total Students"
                  placeholder="e.g. 5000"
                  required
                  disabled={updateMutation.isPending}
                />

                <InputFiled
                  type="url"
                  name="website"
                  label="Website"
                  placeholder="https://www.college.edu"
                  required
                  disabled={updateMutation.isPending}
                />

                <div className="md:col-span-2">
                  <TextArea
                    name="description"
                    label="Description"
                    placeholder="Provide a brief description of the college..."
                    className={{ textareaClass: "min-h-[120px]" }}
                    required
                    disabled={updateMutation.isPending}
                  />
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  size="lg"
                  disabled={updateMutation.isPending}
                  className="min-w-[150px]"
                >
                  {updateMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </PageContainer>
  );
};

export default CollegeProfile;
